/**
 * PORTED FROM Reception (`reception-v2/src/lib/datetime.ts`), not rewritten.
 *
 * The bug it fixes here is the same one, measured again: a scan made seconds ago showed
 * "3 hours ago". The DB writes a naive MySQL DATETIME in the SERVER's zone (Europe/Berlin), the
 * browser parses that string as ITS OWN local time, and the gap between the two zones becomes the
 * error. From New Delhi that is 5:30 − 2:00 = 3h30m — which is exactly what was on screen.
 *
 * Display-only. Nothing about what is stored or queried changes.
 */
/**
 * Timezone-correct date/time handling for DISPLAY.
 *
 * THE PROBLEM (proven on prod 2026-07-28, event 8891307940):
 *   - Registration `created` is a plain MySQL DATETIME, e.g. "2026-07-28 12:59:36", written by the DB
 *     in the DB SERVER's local timezone (Contabo / Europe → CET in winter, CEST in summer). It carries
 *     NO timezone marker.
 *   - api-reception-v2 reads it with mysql2 `timezone:'Z'`, i.e. it LABELS that naive wall-clock as UTC
 *     and ships "2026-07-28T12:59:36.000Z" to the browser. But 12:59 was CEST (=10:59 UTC), so every
 *     timestamp renders ~2h AHEAD of the real moment.
 *
 * THE FIX (display-only; DB / queries / filters / other apps untouched — owner decision 2026-07-28):
 *   Interpret a naive DB DATETIME as `SOURCE_DB_TIMEZONE` (default "Europe/Berlin", the server zone),
 *   recover the true absolute instant, then let the browser render it in the VIEWER's own local zone.
 *   A Muscat user sees Muscat time, a London user sees London time.
 *
 * SCOPE: this is a compatibility shim for LEGACY DATETIME values stored in server-local time. The
 * long-term fix is to store all timestamps in UTC and drop the source-zone assumption. When that happens,
 * set SOURCE_DB_TIMEZONE=UTC (or a value that already carries an offset/`Z` bypasses the shim entirely).
 */

// IANA zone the naive DB DATETIMEs are actually in. Overridable per deploy; default = the current
// prod server zone (Europe/Berlin — DST-aware, so CET↔CEST is handled automatically, unlike a fixed +2).
export const SOURCE_DB_TIMEZONE = process.env.REACT_APP_SOURCE_DB_TIMEZONE || 'Europe/Berlin';

// A naive timestamp is one with NO timezone information: no trailing `Z`, no ±HH:MM offset. Only these
// need the source-zone interpretation. Anything that already carries a zone (ISO with Z/offset, or a JS
// Date / epoch, which are absolute instants) is left exactly as-is.
function isNaiveTimestampString(s) {
  const t = s.trim();
  // MySQL "YYYY-MM-DD HH:MM:SS" or ISO-like "YYYY-MM-DDTHH:MM:SS" WITHOUT a zone suffix.
  return /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?(\.\d+)?$/.test(t);
}

/**
 * Given the numeric parts of a wall-clock time IN a named IANA zone, return the UTC epoch millis for
 * that instant — correctly accounting for that zone's offset (incl. DST) on that date. Uses Intl only
 * (no date library). Works by asking what those UTC parts would render AS in the target zone, measuring
 * the delta, and correcting — the standard two-pass trick, DST-safe.
 */
function zonedWallClockToUtcMs(
  y, mo, d, h, mi, s, tz,
) {
  // First guess: treat the wall-clock parts as if they were UTC.
  const asUtc = Date.UTC(y, mo - 1, d, h, mi, s);
  // What does that instant look like in the target zone? The difference is the zone offset at that time.
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts = dtf.formatToParts(new Date(asUtc));
  const get = (type) => { const hit = parts.find((p) => p.type === type); return Number(hit && hit.value); };
  let hour = get('hour');
  if (hour === 24) hour = 0; // Intl may emit "24" for midnight in some environments.
  const seenAsUtc = Date.UTC(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'));
  const offset = seenAsUtc - asUtc; // tz is `offset` ms ahead of UTC at this instant.
  return asUtc - offset;
}

/**
 * Parse any timestamp we might receive into an absolute JS Date.
 *  - JS Date / epoch number / numeric string  → absolute already, returned as-is.
 *  - ISO string WITH a zone (Z or ±offset)     → absolute already, native parse.
 *  - NAIVE "YYYY-MM-DD[ T]HH:MM:SS" (no zone)   → interpreted in SOURCE_DB_TIMEZONE (the fix).
 * Returns null on failure.
 */
export function parseServerDate(input) {
  if (input == null) return null;
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? null : input;

  if (typeof input === 'number') {
    const ms = input < 1e12 ? input * 1000 : input; // seconds vs millis
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) return null;

    // Pure-numeric string → epoch.
    if (/^\d{10,16}$/.test(trimmed)) {
      const n = Number(trimmed);
      const ms = n < 1e12 ? n * 1000 : n;
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    // Naive DB DATETIME → interpret in the source zone (THE FIX).
    if (isNaiveTimestampString(trimmed)) {
      const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/.exec(trimmed);
      if (m) {
        const ms = zonedWallClockToUtcMs(
          Number(m[1]), Number(m[2]), Number(m[3]),
          Number(m[4]), Number(m[5]), Number(m[6] ?? '0'),
          SOURCE_DB_TIMEZONE,
        );
        const d = new Date(ms);
        return Number.isNaN(d.getTime()) ? null : d;
      }
    }

    // Anything else (ISO with Z/offset, locale strings) → native parse; already absolute.
    const d = new Date(trimmed);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  return null;
}

/** Full date + time in the VIEWER's browser-local zone. "28 Jul 2026, 2:59 PM". */